import { Button, Checkbox, Space } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { translateApiValue } from 'utils/translateApiValue';

import { SelectFilterProps, SelectFilterValue } from './types';

import s from './SelectFilter.module.scss';

const SelectFilter: React.FC<SelectFilterProps> = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  close,
  options,
}) => {
  const { t } = useTranslation();
  const currentValue: SelectFilterValue = React.useMemo(() => {
    if (selectedKeys.length > 0 && typeof selectedKeys[0] === 'string') {
      try {
        return JSON.parse(selectedKeys[0]) as SelectFilterValue;
      } catch {
        return { selected: [] };
      }
    }
    return { selected: [] };
  }, [selectedKeys]);

  const [selected, setSelected] = React.useState<string[]>(currentValue.selected);

  React.useEffect(() => {
    setSelected(currentValue.selected);
  }, [currentValue]);

  const handleApply = React.useCallback(() => {
    if (selected.length > 0) {
      const filterValue: SelectFilterValue = { selected };
      setSelectedKeys([JSON.stringify(filterValue)]);
    } else {
      setSelectedKeys([]);
    }
    
    confirm();
  }, [selected, setSelectedKeys, confirm]);

  const handleReset = React.useCallback(() => {
    setSelected([]);
    clearFilters?.();
  }, [clearFilters]);

  const handleChange = React.useCallback((checkedValues: string[]) => {
    setSelected(checkedValues);
  }, []);

  return (
    <div className={s.selectFilter}>
      <div className={s.selectFilter__content}>
        <Checkbox.Group
          value={selected}
          onChange={handleChange}
          className={s.selectFilter__group}
        >
          <Space direction="vertical" size="small">
            {options.map((option) => (
              <Checkbox key={option.id} value={option.value}>
                {translateApiValue(option.value)}
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      </div>

      <Space className={s.selectFilter__buttons}>
        <Button size="small" onClick={handleReset}>
          {t('filter.reset')}
        </Button>
        <Button type="primary" size="small" onClick={handleApply}>
          {t('filter.apply')}
        </Button>
      </Space>
    </div>
  );
};

export default SelectFilter;
